import { BRAND_CDN } from '@/lib/brand';

/* Single flip-point for the Cultiv marketing site.
   Every route prefix, host and CTA destination lives HERE and only here, so the
   later promotion of these pages to the root route (app/[locale]/) is a git mv
   plus flipping CULTIV_BASE_PATH to '' — no hunting through components. */

/** Canonical production origin (no trailing slash).
 *  Reuses NEXT_PUBLIC_BASE_URL — the origin the whole app already runs on — rather
 *  than introducing a second domain var to keep in sync. Despite the NEXT_PUBLIC_
 *  prefix nothing inlines it into a client bundle: all ~34 consumers are server-side
 *  API routes reading it at runtime, where the deploy-symlinked .env exists. It is
 *  NOT present at build time (buildspec.yml has no env block; .env is symlinked only
 *  at deploy), so the fallback below is what gets baked into anything prerendered —
 *  keep it equal to the production value, and change BOTH in the same release when
 *  the origin moves. ⚠️ .env also interpolates this var into the Cognito redirect
 *  URIs, so moving it repoints OAuth callbacks too — they must be registered in the
 *  Cognito app client before the switch or login breaks. */
export const SITE_BASE = process.env.NEXT_PUBLIC_BASE_URL ?? 'https://cultiv.immo';

/** Route prefix of the marketing pages. Empty since the site moved to the root route
 *  (app/[locale]/(site)/ — a route group adds no URL segment), so cultivPath is now
 *  just /{locale}{sub}. Kept as a constant rather than inlined so the whole site can
 *  be re-nested under a prefix again by changing this one line. */
export const CULTIV_BASE_PATH = '';

export const CULTIV_LOCALES = ['en', 'fr', 'nl'] as const;

/** Locale-prefixed path inside the marketing site: cultivPath('nl', '/platform') → /nl/platform */
export function cultivPath(locale: string, sub = ''): string {
  return `/${locale}${CULTIV_BASE_PATH}${sub}`;
}

export { BRAND_CDN };
export const MARKETING_CDN = `${BRAND_CDN}/marketing`;

/** Demo-booking destination for every primary CTA on the site — the /demo page. */
export function demoHref(locale: string): string {
  return cultivPath(locale, '/demo');
}

/** Gated self-serve product demo (Intercom-style) — the /product-tour page. */
export function watchDemoHref(locale: string): string {
  return cultivPath(locale, '/product-tour');
}

/** The Blog index — /{locale}/blog. */
export function blogHref(locale: string): string {
  return cultivPath(locale, '/blog');
}

/** A single Blog article. The slug is canonical across locales. */
export function blogPostHref(locale: string, slug: string): string {
  return cultivPath(locale, `/blog/${slug}`);
}

/** The Careers index — /{locale}/careers. */
export function careersHref(locale: string): string {
  return cultivPath(locale, '/careers');
}

/** A single job posting. The slug is canonical across locales. */
export function jobHref(locale: string, slug: string): string {
  return cultivPath(locale, `/careers/${slug}`);
}

/** The interview-booking step of a posting. */
export function applyHref(locale: string, slug: string): string {
  return cultivPath(locale, `/careers/${slug}/apply`);
}

/** The single login surface, Cultiv-branded, at the CRM's /auth/login (where all
    auth redirects/logout also land). The old /login marketing route redirects here. */
export function loginHref(locale: string): string {
  return cultivPath(locale, '/auth/login');
}
